import { useGetOrganizationQuery } from '@/api'
import type { RoleSummary, SubAccount } from '@/types'

const NONE: RoleSummary[] = []

/**
 * Team roles the organization may give its members, with the names the
 * platform's super admin gave them. Empty until the organization has loaded,
 * so no screen offers a role the server would refuse.
 *
 * The query is cached by RTK Query, so the team page and the invite dialog
 * share one request.
 */
export function useMemberRoles(): RoleSummary[] {
	const { data: organization } = useGetOrganizationQuery()
	return organization?.roles ?? NONE
}

/** The name of a member's role; the key while the name has not loaded. */
export function memberRoleName(sub: Pick<SubAccount, 'role' | 'roleName'>): string {
	return sub.roleName || sub.role
}

/**
 * The offered roles, with a member's own appended when the organization no
 * longer offers it, so a select still shows what the member holds.
 */
export function withHeldRoles(roles: RoleSummary[], held: Pick<SubAccount, 'role' | 'roleName'>[]): RoleSummary[] {
	const extra = new Map<string, RoleSummary>()
	for (const sub of held) {
		if (!roles.some((r) => r.key === sub.role) && !extra.has(sub.role)) {
			extra.set(sub.role, { key: sub.role, name: memberRoleName(sub) })
		}
	}
	return extra.size ? [...roles, ...extra.values()] : roles
}
